import i18n, { t } from 'i18next';
import { Button, DriftThemeProvider, FormControlLabel, Radio, RadioGroup, Chip, Typography } from 'drift-ui';
import { z } from 'zod';
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import styles from '../styles/ptEnterPayment.scss';
import Toggle from '../../core/toggle';
import greenCheckmark from '../../../assets/images/green-checkmark.svg';
import YearlyDiscount from './yearlyDiscount';
import { useForm, FieldErrors, Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import '../../../hooks/zod';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../bootstrap';
import CreditCardForm, { CreditCardFormData } from './CreditCardForm';
import FactoringFundsForm, { FactoringFundsFormData } from './FactoringFundsForm';
import { FF } from '../../../utils/utils';
import { User } from 'interfaces/user';
import { formatPhone } from '../utils/utils';
import commonStyles from '../styles/common.scss';
import {
  BinLookupResponse,
  CreatePTSubscriptionFromFactoringFundsPayload,
  CreatePTSubscriptionPayload,
  useBinLookupMutation,
  useCreatSubscriptionMutation,
  useGetPtSubscriptionPricingQuery,
  useCreateSubscriptionFromFactoringFundsMutation,
  useCheckSubscriptionHistoryQuery,
  useCreatePtPaymentProfileMutation,
  useDeletePaymentProfileMutation,
} from '../../../state/paymentService';
import FirebaseAnalytics from '@shamrock-core/common/analytics/firebase-analytics';
import { useTranslation } from 'react-i18next';

const Config = require('Config');

interface PtEnterPaymentProps {
  isMonthly: boolean;
  setIsMonthly: (isMonthly: boolean) => void;
  handleNext: () => void;
  handleBack: () => void;
}

type FormData = CreditCardFormData | FactoringFundsFormData;

const isAmexCard = (cardNumber?: string) => {
  if (!cardNumber) {
    return false;
  }
  return cardNumber.startsWith('34') || cardNumber.startsWith('37');
};

const addCardSchema = z.object({
  cardNumber: z
    .string()
    .min(1, { message: t('Required field') })
    .refine(
      (value: string): boolean => {
        if (!value) return false;
        if (isAmexCard(value)) {
          return /^\d{4}-\d{6}-\d{5}$/.test(value) || /^\d{15}$/.test(value);
        }
        return /^\d{4}-\d{4}-\d{4}-\d{4}$/.test(value) || /^\d{16}$/.test(value);
      },
      {
        message: t('Enter 16 digits'),
      },
    ),
  expirationDate: z
    .string()
    .min(1, { message: t('Required field') })
    .regex(/^\d\d\/\d\d$/, { message: 'mm / yy' })
    .refine(
      (val: string): boolean => {
        const [mm] = val?.split('/') ?? [''];
        const month = parseInt(mm);
        return month > 0 && month < 13;
      },
      { message: 'mm / yy' },
    )
    .refine(
      (val) => {
        const [mm, yy] = val?.split('/') ?? ['', ''];
        const month = parseInt(mm);
        const currentCentury = dayjs().year().toString().slice(0, 2);
        const year = parseInt(`${currentCentury}${yy}`);
        return dayjs().isBefore(
          dayjs()
            .month(month - 1)
            .year(year)
            .add(1, 'month')
            .startOf('month'),
        );
      },
      { message: t('Date has expired') },
    ),
  securityCode: z
    .string()
    .min(1, { message: t('Required field') })
    .regex(/^\d\d\d\d?$/, { message: t('Enter 3 to 4 digits') }),
  firstName: z.string().min(1, { message: t('Required field') }),
  lastName: z.string().min(1, { message: t('Required field') }),
  billingAddress: z.string().min(1, { message: t('Required field') }),
  billingState: z.string().min(1, { message: t('Required field') }),
  zipCode: z
    .string()
    .min(1, { message: t('Required field') })
    .regex(/^\d\d\d\d\d$/, { message: t('Enter 5 digits') }),
  agreementOfPurchase: z.string().min(1, { message: t('You must agree to the terms') }),
});

const factoringFundsSchema = z.object({
  firstName: z.string().min(1, { message: t('Required field') }),
  lastName: z.string().min(1, { message: t('Required field') }),
  companyName: z.string().min(1, { message: t('Required field') }),
  email: z
    .string()
    .email({ message: t('Please enter a valid email address.') as string })
    .max(255),
  phoneNumber: z.preprocess(
    (val) => (val as string)?.replace(/[()\s-]/g, ''),
    z.string().regex(/^\d{10}$/, { message: t('Please enter a valid phone number.') as string }),
  ),
  agreementOfPurchase: z.string().min(1, { message: t('You must agree to the terms') }),
  agreementOFactoringDeduction: z.string().min(1, { message: t('You must agree to the terms') }),
});

export default function PtEnterPayment({ isMonthly, setIsMonthly, handleBack, handleNext }: PtEnterPaymentProps) {
  const { t } = useTranslation('common', { keyPrefix: 'proTransportSubscriptions' });
  const [error, setError] = useState<{ mainText: string; subText: string } | null>(null);
  const user = useSelector<RootState, User>((state) => state.app.user);
  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const [isCreditCard, setIsCreditCard] = useState(true);
  const [isAcceptJsLoaded, setIsAcceptJsLoaded] = useState(false);
  const authorizeProdSandbox = features.includes(FF.AUTHORIZE_NET_PROD_SANDBOX);
  const { data: ptSubscriptionPricing } = useGetPtSubscriptionPricingQuery();
  const [binLookup] = useBinLookupMutation();
  const [createSubscription] = useCreatSubscriptionMutation();
  const [createSubscriptionFromFactoringFunds] = useCreateSubscriptionFromFactoringFundsMutation();
  const { data: hadPreviousSubscription } = useCheckSubscriptionHistoryQuery();
  const [createPaymentProfile] = useCreatePtPaymentProfileMutation();
  const [deletePaymentProfile] = useDeletePaymentProfileMutation();
  const cost = isMonthly
    ? parseFloat(ptSubscriptionPricing?.premium.monthly).toFixed(2)
    : parseFloat(ptSubscriptionPricing?.premium.yearly).toFixed(2);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(isCreditCard ? addCardSchema : factoringFundsSchema),
    mode: 'onTouched',
    delayError: 400,
  });
  const cardNumber = isCreditCard ? watch('cardNumber' as keyof CreditCardFormData) : undefined;
  const expirationDate = isCreditCard ? watch('expirationDate' as keyof CreditCardFormData) : undefined;

  useEffect(() => {
    const newDefaultValues = isCreditCard
      ? {
          firstName: '',
          lastName: '',
        }
      : {
          firstName: user?.firstname || '',
          lastName: user?.lastname || '',
          phoneNumber: formatPhone(user?.phone) || '',
          companyName: user?.company || '',
          email: user?.email || '',
        };

    reset(newDefaultValues);
  }, [isCreditCard, user, reset]);

  useEffect(() => {
    if (document.querySelector(`script[src="${Config.acceptJS}"]`)) {
      setIsAcceptJsLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = authorizeProdSandbox ? Config.acceptJSSandbox : Config.acceptJS;
    script.async = true;
    script.onload = () => setIsAcceptJsLoaded(true);
    document.head.appendChild(script);
  }, []);

  const onCreditCardSubmit = async (data: CreditCardFormData) => {
    if (!isAcceptJsLoaded) {
      throw new Error('Accept.js is not loaded yet. Please try again later.');
    }

    const creditCardData = data as CreditCardFormData;
    const unformattedCardNumber = creditCardData.cardNumber?.replace(/-/g, '');
    const [month, year] = creditCardData.expirationDate?.split('/');
    const cardData = {
      cardCode: creditCardData.securityCode,
      cardNumber: unformattedCardNumber,
      month,
      year,
      zip: creditCardData.zipCode,
    };
    const authData = {
      apiLoginID: authorizeProdSandbox ? Config.authorizeNetSandboxApiLoginId : Config.authorizeNetApiLoginId,
      clientKey: authorizeProdSandbox ? Config.authorizeNetSandboxClientKey : Config.authorizeNetClientKey,
    };
    const secureData = { authData, cardData };

    const result: any = await new Promise((resolve) => {
      (window as any).Accept.dispatchData(secureData, resolve);
    });
    const binData: BinLookupResponse = await binLookup({
      cardNumber: unformattedCardNumber?.substring(0, 8) as string,
    }).unwrap();
    const createPaymentProfilePayload = {
      cardData: result.opaqueData.dataValue,
      addressInfo: {
        firstName: creditCardData.firstName,
        lastName: creditCardData.lastName,
        address: creditCardData.billingAddress,
        state: creditCardData.billingState,
        zip: creditCardData.zipCode,
      },
      cardPaymentType: binData['card-type'] ?? '',
      neutrinoData: binData ?? {},
      agreementOfPurchase: creditCardData.agreementOfPurchase,
    };

    const profile = await createPaymentProfile(createPaymentProfilePayload).unwrap();
    const createSubscriptionPayload: CreatePTSubscriptionPayload = {
      firstName: creditCardData.firstName,
      lastName: creditCardData.lastName,
      lastFour: `****${unformattedCardNumber.slice(-4)}`,
      subscriptionName: 'PT Premium',
      paymentTerm: isMonthly ? 'monthly' : 'yearly',
      paymentProfileId: profile.newPaymentProfile.customerPaymentProfileId,
    };
    try {
      await createSubscription(createSubscriptionPayload).unwrap();
    } catch (error) {
      await deletePaymentProfile({
        paymentProfileId: profile.newPaymentProfile.customerPaymentProfileId,
        source: 'pt-subscription',
      }).unwrap();
      throw error;
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    FirebaseAnalytics.sendEvent(
      FirebaseAnalytics.EVENTS.ENTER_PAYMENT_INFORMATION,
      FirebaseAnalytics.MODULES.RTSPRO,
      FirebaseAnalytics.PAGES.ANY,
      {
        Description: 'Pay Click',
        PaymentTerm: isMonthly ? 'Monthly' : 'Yearly',
        PaymentMethod: isCreditCard ? 'Credit Card' : 'Factoring Funds',
      },
    );
    try {
      if (isCreditCard) {
        await onCreditCardSubmit(data as CreditCardFormData);
      } else {
        const factoringData = data as FactoringFundsFormData;
        const payload: CreatePTSubscriptionFromFactoringFundsPayload = {
          subscriptionName: 'PT Premium',
          paymentTerm: isMonthly ? 'monthly' : 'yearly',
          email: factoringData.email,
          firstname: factoringData.firstName,
          lastname: factoringData.lastName,
          company: factoringData.companyName,
          phone: factoringData.phoneNumber.replaceAll(/\D/g, ''),
          device_info: {
            type: 'web',
            os: window.navigator.userAgent,
            device_language: i18n.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          purchase_acceptance: factoringData.agreementOfPurchase,
          cost_acceptance: factoringData.agreementOFactoringDeduction,
        };
        await createSubscriptionFromFactoringFunds(payload).unwrap();
      }
      handleNext();
    } catch (error) {
      if (error.message?.includes('Accept.js')) {
        setError({
          mainText: t('Loading Error'),
          subText: t('Please refresh your browser to try again.'),
        });
      } else {
        setError({
          mainText: t('Payment Failed'),
          subText: t('Payment failed to process. Verify all information has been entered correctly and try again.'),
        });
      }
    }
  });

  return (
    <DriftThemeProvider>
      <div className={styles.container}>
        <div className={styles.leftSection}>
          <div>
            <div className={styles.productBox}>
              <div className={styles.productName}>
                {t('ProTransport Premium')}
                <img src={greenCheckmark} className={styles.checkmark} alt="Selected" />
              </div>
              <div className={styles.productPrice}>{`$${cost} / ${isMonthly ? 'mo.' : 'year'}`}</div>
              {!hadPreviousSubscription && (
                <Chip
                  className={styles.trialChip}
                  color="success"
                  label={t('free_trial')}
                  variant="filled"
                  size="small"
                />
              )}
            </div>
            <div className={styles.featureList}>
              <div className={styles.featureTitle}>{t('included_with_premium')}</div>
              <div className={styles.featureSubtitle}>{t('everything_in_basic_plus')}</div>
              <div className={styles.featureItem}>{t('aging_reports')}</div>
              <div className={styles.featureItem}>{t('driver_pay_statements')}</div>
              <div className={styles.featureItem}>{t('wex_fuel_import')}</div>
              <div className={styles.featureItem}>{t('dedicated_implementation')}</div>
            </div>
            <button className={styles.backButton} onClick={handleBack}>
              &lt; {t('Select plan')}
            </button>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.header}>{t('Enter Payment Information')}</div>
          <div className={commonStyles.subHeader}>{t('Payment Method')}</div>
          <RadioGroup
            row
            defaultValue="credit_card"
            style={{ display: 'flex', paddingLeft: 0, marginTop: '-12px' }}
            name="row-radio-buttons-group"
            onChange={(event) => setIsCreditCard(event.target.value === 'credit_card')}
          >
            <FormControlLabel value="credit_card" control={<Radio />} label={t('Pay with credit card')} />
            <FormControlLabel value="factoring_funds" control={<Radio />} label={t('Pay with factoring funds')} />
          </RadioGroup>

          <div className={commonStyles.subHeader}>{t('Subscription')}</div>
          <div className={styles.toggle}>
            <Toggle
              leftText={t('Monthly')}
              rightText={t('Yearly')}
              onChange={(index) => setIsMonthly(index === 0)}
              selectedIndex={isMonthly ? 0 : 1}
              style={{ display: 'flex', marginLeft: 0 }}
            />
            <YearlyDiscount amount={10} />
          </div>
          {isCreditCard ? (
            <CreditCardForm
              control={control as Control<CreditCardFormData>}
              errors={errors as FieldErrors<CreditCardFormData>}
              cardNumber={cardNumber as string}
              expirationDate={expirationDate as string}
            />
          ) : (
            <FactoringFundsForm
              cost={cost}
              isMonthly={isMonthly}
              control={control as Control<FactoringFundsFormData>}
              errors={errors as FieldErrors<FactoringFundsFormData>}
            />
          )}
          <div className={styles.details}>
            <div className={styles.detailsHeader}>{t('ProTransport Premium')}</div>
            <div className={commonStyles.row}>
              <div className={styles.detailsDuration}>
                {isMonthly ? t('Monthly Subscription') : t('Yearly Subscription')}
              </div>
              <div className={styles.detailsAmountPriceWrapper}>
                <div className={styles.detailsAmount}>{t('Amount')} :</div>
                <div className={styles.detailsPrice}>${cost}</div>
              </div>
            </div>
          </div>
          {error && (
            <>
              <Typography color="error" fontWeight="500" marginTop="-20">
                {error.mainText}
              </Typography>
              <Typography color="error" fontSize="14" marginBottom="20">
                {error.subText}
              </Typography>
            </>
          )}
          <Button
            size="large"
            variant="contained"
            color="primary"
            disabled={!isValid}
            fullWidth
            onClick={onSubmit}
            loading={isSubmitting}
          >
            {t('Pay')}
          </Button>
          <div className={styles.disclaimer}>
            {t(
              // eslint-disable-next-line max-len
              "I understand each month's payment equal to the above amount will be debited from my bank account under the name of ProTransport Inc., on the subscription date. I understand the amount of my payment may change periodically. Notice of payment changes will be provided to me before the effective date of each change.",
            )}
          </div>
        </div>
      </div>
    </DriftThemeProvider>
  );
}
